import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { logo } from '../../images/images.js'

import axios from '../../dataClient/dataClient'
import { postPIRData, postUploadPIRFile } from '../../dataClient/apiCalls'
import { isPirPaymentEnabled } from '../../util/pirConsts.js'
import PaymentConfirmation from '../payments/PaymentConfirmation'
import { config } from '../../config'
import { formatPIRForReview } from '../../util/pirFormatting.js'
import { PageWithTitle, Logo, Header } from '../styles/Containers'
import { PageTitle } from '../styles/Text'
import PIRForm from '../pir/PIRForm.js'
import PIRReview from '../pir/PIRReview.js'
import { getRuntimeEnv } from '../../runtimeEnv'
import pirTerminology from '../../util/pirTerminology'

const BASE_URL = getRuntimeEnv('REACT_APP_API_URL')
const OWNER_VERIFICATION = config.pir.gis.ownerVerification
const ADDRESS_VERIFICATION = config.pir.gis.addressVerification
const STRATA_VERIFICATION = config.pir.gis.strataVerification
const SIGNATURE_DISPATCH_FAILED = 'SIGNATURE_DISPATCH_FAILED'

function determineDefaultStrataState({ state }) {
  if (ADDRESS_VERIFICATION && STRATA_VERIFICATION) {
    if (!state) return 'info'
    if (state?.pir.strataInformation.role !== '') return 'autoStrata'
    return 'autoNoStrata'
  } else {
    if (!state) return 'manualUnset'
    if (state?.pir.strataInformation.role !== '') return 'manualStrata'
    return 'manualNoStrata'
  }
}

function PIRPage() {
  const navigate = useNavigate()
  const { state } = useLocation()

  const defaultPir = state && state.pir ? state.pir : undefined
  const defaultStrataState = determineDefaultStrataState({ state })

  const [visiblePage, setVisiblePage] = useState(defaultPir ? 'review' : 'form')
  const [pir, setPir] = useState(defaultPir)
  const [strataState, setStrataState] = useState(defaultStrataState)
  const [submissionLoading, setSubmissionLoading] = useState()
  const [submissionError, setSubmissionError] = useState()
  const [fileTypeErrorMessage, setFileTypeErrorMessage] = useState(null)
  const [pendingSubmission, setPendingSubmission] = useState(null)
  const [ownerMatches, setOwnerMatches] = useState()
  const [formattedAddress, setFormattedAddress] = useState()

  // This resets the form by clearing the page state on refresh
  useEffect(() => {
    window.onbeforeunload = function () {
      navigate('/')
    }
  }, [])

  useEffect(() => {
    if (defaultPir && OWNER_VERIFICATION) determineOwnerMatches({ pir: defaultPir })
  }, [defaultPir])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [visiblePage])

  function determineOwnerMatches({ pir }) {
    axios
      .post(`${BASE_URL}/property-owner`, {
        address: pir.propertyInformation.address,
        unit: pir.propertyInformation.unit,
        firstName: pir.ownerInformation.firstName,
        lastName: pir.ownerInformation.lastName,
        companyName: pir.ownerInformation.companyName,
        formattedAddress: formattedAddress || pir.propertyInformation.formattedAddress
      })
      .then(({ data }) => {
        setOwnerMatches(data.data.ownerMatches)
      })
      .catch(e => {
        console.error('Failed verifying owners', e)
      })
  }

  function handleCompleteForm({ pir }) {
    setPir(pir)
    setStrataState(pir.strataState)
    if (OWNER_VERIFICATION) determineOwnerMatches({ pir })
    setVisiblePage('review')
  }

  function handleEdit() {
    setPendingSubmission(null)
    setSubmissionError()
    setFileTypeErrorMessage(null)
    setVisiblePage('form')
  }

  async function handleSubmitPir() {
    setSubmissionLoading(true)
    setSubmissionError()

    const payload = structuredClone(pir)

    payload.ownerInformation.signatureSource = payload.ownerInformation.signatureSource.toUpperCase()

    if (!['autoStrata', 'manualStrata'].includes(strataState)) {
      delete payload.strataInformation
    } else {
      delete payload.strataInformation.confirmEmail
    }

    if (pir.ownerInformation.ownedBy === 'person') delete payload.ownerInformation.companyName
    delete payload.ownerInformation.ownedBy
    delete payload.ownerInformation.signatureFile
    delete payload.ownerInformation.confirmEmail

    if (payload.ownerInformation.signatureSource === 'UPLOAD') {
      delete payload.ownerInformation.email
      delete payload.ownerInformation.phone
    }

    if (!payload.requestorInformation.isRealtor) payload.requestorInformation.brokerageName = ''
    delete payload.requestorInformation.confirmEmail

    if (payload.additionalRecipients.length > 0) payload.additionalRecipients = payload.additionalRecipients.map(p => p.email)
    else delete payload.additionalRecipients

    if (payload.requestInformation) {
      const { tier, additionalDetails, tierOptions } = payload.requestInformation
      const isEmpty = (!tier || tier === '') && (!additionalDetails || additionalDetails.trim() === '') && (!tierOptions || tierOptions.length === 0)
      if (isEmpty) {
        delete payload.requestInformation
      }
    }

    delete payload.strataState
    delete payload.propertyInformation.formattedAddress

    if (pendingSubmission?.action === 'submit') {
      payload.retryPirId = pendingSubmission.pirId
    }

    const createRequest =
      pendingSubmission?.action === 'upload' ? Promise.resolve({ data: { pirId: pendingSubmission.pirId } }) : postPIRData(payload)

    createRequest
      .then(async response => {
        const pirId = response.data.pirId

        if (!isPirPaymentEnabled()) {
          const signatureFile = pir.ownerInformation.signatureFile
          if (pir.ownerInformation.signatureSource === 'upload' && signatureFile) {
            try {
              await postUploadPIRFile(pirId, signatureFile, true, 'owner')
            } catch (uploadError) {
              console.error('failed to upload the owner signature file for PIR with ID', pirId, uploadError)
              setPendingSubmission({ pirId, action: 'upload' })
              setSubmissionError('signatureUpload')
              return
            }
          }

          setPendingSubmission(null)
          setVisiblePage('confirmation')
          return
        }

        if (pir.ownerInformation.signatureSource === 'upload') {
          navigate(`/payment/${pirId}`, { state: { ownerSigFile: pir.ownerInformation.signatureFile } })
        } else {
          navigate(`/payment/${pirId}`)
        }
      })
      .catch(e => {
        console.error('Failed posting PIR data to server', e)

        if (e.response?.data?.code === SIGNATURE_DISPATCH_FAILED && e.response.data.pirId) {
          setPendingSubmission({ pirId: e.response.data.pirId, action: 'submit' })
          setSubmissionError('signatureDispatch')
        } else if (e.response?.data?.name === 'FileTypeError') {
          setSubmissionError('fileType')
          setFileTypeErrorMessage(e.response.data.message)
        } else if (e.response && e.response.data.message === 'address not found') {
          setSubmissionError('address')
        } else {
          setSubmissionError('other')
        }
      })
      .finally(() => setSubmissionLoading(false))
  }

  if (visiblePage === 'confirmation') return <PaymentConfirmation paid={false} />

  return (
    <PageWithTitle gap="40px">
      <Header>
        <Logo src={logo}></Logo>
        <PageTitle>{`${pirTerminology.getRequestName()}${visiblePage === 'review' ? ' Review' : ''}`}</PageTitle>
      </Header>
      {visiblePage === 'form' && (
        <PIRForm
          key={pir?.requestInformation?.tier}
          pir={pir}
          strata={strataState}
          onCompleteForm={handleCompleteForm}
          saveFormattedAddress={setFormattedAddress}
          addressVerification={ADDRESS_VERIFICATION}
          strataVerification={STRATA_VERIFICATION}
        />
      )}
      {visiblePage === 'review' && (
        <PIRReview
          pir={formatPIRForReview(pir, strataState)}
          onBack={handleEdit}
          onNext={handleSubmitPir}
          submissionLoading={submissionLoading}
          submissionError={submissionError}
          editDisabled={pendingSubmission?.action === 'upload'}
          fileTypeErrorMessage={fileTypeErrorMessage}
          ownerMatches={ownerMatches}
        />
      )}
    </PageWithTitle>
  )
}

export default PIRPage
